import { handleAddEventBoardView, handleUpdateEventBoardView } from '@/utilities/moveCard';
import { isMember, isTag } from '@/utilities/cardFilterMethods';
import { groupByOption } from '@/types/groupByOption';
import { ResourceType } from '@/types/resources';
import { BoardLayout } from '@superthread-com/api';
import { getSprintListKey } from '@/utilities/sprintKeys';
import { globalState } from '@/utilities/globalState';

let updateTimeout = null;

export default {
  computed: {
    listCards: {
      get() {
        return this.sortedCards;
      },
      set() {
        // This is intentional settter that doesn't mutate listCards
      },
    },
    currentBoard() {
      const embed = this.embed ?? this._setupProxy?.embed.value;
      if (embed) {
        const embeddedBoardAttributes =
          this.embeddedBoardAttributes ?? this._setupProxy.embeddedBoardAttributes.value;
        return this.$store.getters.getFlatBoards[embeddedBoardAttributes.boardId] || {};
      }
      if (!this.visible && !this.isCardsLayoutCalendar) {
        return {};
      }

      return this.$store.getters.currentBoard;
    },

    currentBoardId() {
      const embed = this.embed ?? this._setupProxy?.embed.value;
      if (embed) {
        const embeddedBoardAttributes =
          this.embeddedBoardAttributes ?? this._setupProxy.embeddedBoardAttributes.value;
        return embeddedBoardAttributes.boardId;
      }
      return this.$store.getters.currentBoardId;
    },

    teamId() {
      return this.$store.getters.getTeamID;
    },
    listDragging() {
      return (
        this.draggingIsOn &&
        this.draggedEntity.entityName === 'list' &&
        this.draggedEntity.id === this.list.id
      );
    },
    cardDragging() {
      return (
        this.draggingIsOn &&
        [ResourceType.Card, ResourceType.Epic].includes(this.draggedEntity.entityName)
      );
    },

    epicId() {
      if (this.inEpicBoard) {
        return this.currentBoardId.split('-')[1] || '';
      }

      return '';
    },

    cards() {
      if (this.inEpicBoard) {
        return this.$store.getters.getCardsByEpicId(this.epicId) || {};
      }

      return this.$store.getters.getCards;
    },

    currentFilters() {
      const boardId =
        this.boardGroupBy !== groupByOption.status ||
        this.isSprint ||
        this.isCardsLayoutCalendar ||
        this.subGroupByEnabled
          ? this.currentBoard.id
          : this.list?.board_id;
      if (this.isViewOrMyWork) {
        return this.$store.getters.getViewFilters;
      }

      if (!boardId) {
        return { include: {}, exclude: {} };
      }
      return this.$store.getters.boardFilters(boardId);
    },

    filteredListCards() {
      if (this.visible != null && this.visible === false && this.largeBoard) {
        if (this.draggedEntity.listId !== this.list.id) {
          return [];
        }
      }
      const cardIds = this.originalListCards;
      if (!cardIds) return [];
      return cardIds
        .filter((cardId) => {
          const card = this.cards[cardId];
          if (!card) {
            return false;
          }

          return (
            this.isNoAssignees(cardId) &&
            isMember(cardId, this.currentFilters) &&
            this.isCreatorOrOwner(cardId, 'owners', 'owner_id') &&
            this.isCreatorOrOwner(cardId, 'creators', 'user_id') &&
            isTag(cardId, this.currentFilters) &&
            this.isCardParent(cardId) &&
            this.isChildCard(cardId) &&
            this.isArchivedCard(cardId) &&
            this.isStatuses(cardId) &&
            this.isDueDate(cardId) &&
            this.isLink(cardId) &&
            this.isTime(cardId, 'time_created') &&
            this.linkedCardFilter(cardId) &&
            this.isTime(cardId, 'time_updated') &&
            this.isPriority(cardId) &&
            this.isEstimate(cardId) &&
            this.isParentCard(cardId) &&
            this.isEpic(cardId) &&
            this.isProject(cardId) &&
            this.isBoard(cardId) &&
            this.isCardTitleMatch(cardId)
          );
        })
        .filter(Boolean);
    },

    boardGroupByOrder() {
      return (
        this.groupByObject ?? this.$store.getters.getBoardGroupByObject(this.currentBoard.id) ?? []
      );
    },

    listCardsGroupByOrder() {
      const cardOrder = this.$store.getters.getGroupByCardOrder(
        this.currentBoardId,
        this.boardGroupBy,
        this.listId
      );
      if (cardOrder.length) {
        return cardOrder;
      }
      return this.boardGroupByOrder[this.listId]?.cardIds || [];
    },

    boardGroupBy() {
      const embed = this.embed ?? this._setupProxy?.embed.value;
      const inEpicBoard = this.inEpicBoard ?? this._setupProxy?.inEpicBoard.value;
      if (embed && !inEpicBoard) {
        const embeddedBoardAttributes =
          this.embeddedBoardAttributes ?? this._setupProxy.embeddedBoardAttributes.value;
        return embeddedBoardAttributes?.groupBy || groupByOption.status;
      }

      return this.$store.getters.getBoardGroupBy(this.currentBoardId) || groupByOption.status;
    },

    boardSubGroupBy() {
      const embed = this.embed ?? this._setupProxy?.embed.value;
      const inEpicBoard = this.inEpicBoard ?? this._setupProxy?.inEpicBoard.value;
      if (embed && !inEpicBoard) {
        const embeddedBoardAttributes =
          this.embeddedBoardAttributes ?? this._setupProxy.embeddedBoardAttributes.value;
        return embeddedBoardAttributes?.subGroupBy || groupByOption.status;
      }

      return this.$store.getters.getBoardSubGroupBy(this.currentBoardId) || groupByOption.status;
    },

    subGroupByEnabled() {
      if (this.embed && !this.isSprint) {
        return this.embeddedBoardAttributes?.enableSubGroupBy;
      }
      return this.$store.getters.getSubGroupByEnabled(this.currentBoardId);
    },

    subGroupByListCardIds() {
      return this.subGroupCards ?? [];
    },

    originalListCards() {
      if (this._setupProxy?.boardCards.value) {
        return this._setupProxy.boardCards.value;
      }
      if (this.visible != null && this.visible === false && this.largeBoard) {
        if (this.draggedEntity.listId !== this.listId) {
          return [];
        }
      }

      if (!this.subGroupByEnabled && this.boardGroupBy !== groupByOption.status) {
        return this.listCardsGroupByOrder;
      }

      if (this.subGroupByEnabled) {
        return this.subGroupByListCardIds;
      }

      return this.$store.getters.getListCards[this.listId] || [];
    },

    lessFilteredCards() {
      return this.filteredListCards.length < this.originalListCards.length;
    },
    dropTargetId() {
      return this.$store.getters.dropTargetId;
    },

    isViewOrMyWork() {
      return (
        this.isViewOpen ||
        [(this.$constants.myWorkRouteName, this.$constants.memberRouteName)].includes(
          this.$route.name
        )
      );
    },

    boardSortBy() {
      if (this.isCardsLayoutCalendar) {
        return '';
      }

      const embed = this.embed ?? this._setupProxy?.embed;
      const inEpicBoard = this.inEpicBoard ?? this._setupProxy?.inEpicBoard;
      if (embed && !inEpicBoard) {
        const embeddedBoardAttributes =
          this.embeddedBoardAttributes ?? this._setupProxy.embeddedBoardAttributes;
        return embeddedBoardAttributes?.sortBy;
      }

      return this.$store.getters.getBoardSortBy(this.currentBoardId);
    },

    sortedCards() {
      switch (this.boardSortBy) {
        case 'due_date':
          return this.filteredListCards.sort((a, b) => {
            const dueDateA = this.cards[a].due_date || Number.MAX_SAFE_INTEGER;
            const dueDateB = this.cards[b].due_date || Number.MAX_SAFE_INTEGER;

            // If due dates are the same, sort by title
            if (dueDateA === dueDateB) {
              const titleA = this.cards[a].title.toLowerCase();
              const titleB = this.cards[b].title.toLowerCase();
              return titleA.localeCompare(titleB);
            }

            return dueDateA - dueDateB;
          });
        case 'time_created':
          return this.filteredListCards.sort(
            (a, b) => this.cards[b].time_created - this.cards[a].time_created
          );
        case 'time_updated':
          return this.filteredListCards.sort(
            (a, b) => this.cards[b].time_updated - this.cards[a].time_updated
          );
        case 'priority':
          const filteredCardsOriginal = this.originalListCards.filter((value) =>
            this.filteredListCards.includes(value)
          );
          return filteredCardsOriginal.sort((a, b) => {
            const priorityA = this.cards[a]?.priority || 0;
            const priorityB = this.cards[b]?.priority || 0;
            return priorityB - priorityA;
          });
        case 'estimate':
          return this.originalListCards
            .filter((value) => this.filteredListCards.includes(value))
            .sort((a, b) => {
              const estimateA = this.cards[a]?.estimate || 0;
              const estimateB = this.cards[b]?.estimate || 0;
              return estimateB - estimateA;
            });
        default:
          if (this.embeddedBoardAttributes?.disableFilter) {
            return this.originalListCards;
          }
          const filteredCards = this.originalListCards.filter((value) =>
            this.filteredListCards.includes(value)
          );
          return filteredCards;
      }
    },

    isSprint() {
      return this.$route.name === this.$constants.routeNames.sprint || this.inboxSprint;
    },

    inboxSprint() {
      return (
        this.$route.name === this.$constants.routeNames.inbox &&
        this.$store.getters.getCurrentInboxResource.resource_type === ResourceType.Sprint
      );
    },
    cardsLayout() {
      const embed = this.embed ?? this._setupProxy?.embed.value;
      if (embed) {
        const embeddedBoardAttributes =
          this.embeddedBoardAttributes ?? this._setupProxy.embeddedBoardAttributes.value;
        return (
          embeddedBoardAttributes.layout || this.$store.getters.cardsLayout(this.currentBoardId)
        );
      }
      return this.$store.getters.cardsLayout();
    },
    isCardsLayoutCalendar() {
      return this.cardsLayout === BoardLayout.Calendar;
    },

    cardsFuse() {
      return this.$store.getters.cardsFuse;
    },

    cardTitleSearchQuery() {
      return this.currentFilters?.include?.title ?? '';
    },

    cardTitleMatches() {
      // important to check if searchQuery is not empty to avoid unnecessary fuse search
      if (!this.cardTitleSearchQuery && !globalState.searchBoardQuery) return [];

      const searchTerms = [this.cardTitleSearchQuery, globalState.searchBoardQuery].filter(Boolean);

      const isInEpicBoard = this.currentBoardId.includes(this.$constants.inEpicBoardIdPrefix);

      if (searchTerms.length === 0) return [];

      const searchParam = {
        $and: [
          ...searchTerms.map((term) => ({ title: term })),
          ...(this.isSprint || isInEpicBoard ? [] : [{ board_id: this.currentBoardId }]),
        ],
      };
      return this.cardsFuse.search(searchParam).map(({ item }) => item.id);
    },
  },

  methods: {
    isCardTitleMatch(cardId) {
      if (
        !this.cardTitleMatches.length &&
        !this.cardTitleSearchQuery &&
        !globalState.searchBoardQuery
      )
        return true;
      return this.cardTitleMatches?.includes?.(cardId);
    },
    isStatuses(cardId) {
      const card = this.cards[cardId];
      const listId = card.sprint_id
        ? getSprintListKey(card.project_id, card.sprint_id, card.list_id)
        : card.list_id;
      const cardStatus = this.$store.getters.lists[listId]?.behavior || card.status || 'backlog';

      if (this.currentFilters?.has_status !== undefined) {
        return this.currentFilters?.has_status && cardStatus === 'backlog' ? false : cardId;
      }

      if (this.currentFilters?.include?.statuses?.length) {
        if (this.currentFilters?.include.statuses.includes(cardStatus)) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.exclude?.statuses?.length) {
        if (this.currentFilters?.exclude.statuses.includes(cardStatus)) {
          return false;
        }
        return cardId;
      }
      return cardId;
    },

    isChildCard(cardId) {
      if (this.currentFilters?.is_child) {
        const card = this.cards[cardId];
        if (Object.keys(card.parent_card || {}).length) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.is_child === false) {
        const card = this.cards[cardId];
        if (!Object.keys(card.parent_card || {}).length) {
          return cardId;
        }
        return false;
      }
      return cardId;
    },

    isCardParent(cardId) {
      if (this.currentFilters?.is_parent) {
        const card = this.cards[cardId];
        // if there is any card that is not archived, that means it has child cards
        const hasChildCard = card?.child_cards?.some((c) => {
          return !this.cards?.[c.card_id]?.archived;
        });
        if (!hasChildCard) return false;
        if (card.child_cards?.length > 0) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.is_parent === false) {
        const card = this.cards[cardId];
        // if all child cards are archived, then it is not a parent card
        const allCardAreArchived = card?.child_cards?.every((c) => {
          return this.cards[c.card_id]?.archived;
        });
        if (card.child_cards?.length === 0 || allCardAreArchived) {
          return cardId;
        }
        return false;
      }
      return cardId;
    },

    isNoAssignees(cardId) {
      if (this.currentFilters?.has_members === false) {
        const card = this.cards[cardId];
        const hasMemberIncluded = card.members?.some((m) =>
          this.currentFilters?.include?.members?.includes(m.user_id)
        );

        if (!card?.members?.length || hasMemberIncluded) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.has_members) {
        const card = this.cards[cardId];
        if (card?.members?.length) {
          return cardId;
        }
        return false;
      }
      return cardId;
    },

    isCreatorOrOwner(cardId, filterType, key) {
      if (this.currentFilters?.include?.[filterType]?.length) {
        const card = this.cards[cardId];
        if (this.currentFilters.include[filterType].includes(card[key])) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.exclude?.[filterType]?.length) {
        const card = this.cards[cardId];
        if (this.currentFilters.exclude[filterType].includes(card[key])) {
          return false;
        }
        return cardId;
      }
      return cardId;
    },

    isDueDate(cardId) {
      if (!Object.keys(this.currentFilters?.due_date || {}).length) {
        return cardId;
      }

      const { before, after } = this.currentFilters.due_date || {};
      const card = this.cards[cardId];

      // no due date filter => show cards with no due date
      if (before === '0') {
        return card.due_date > 0 ? false : cardId;
      }

      // no due date filter => show cards with due date
      if (after === '0') {
        return card.due_date > 0 ? cardId : false;
      }

      const amountAndType = (before || after).replace('now', '');

      // any other due date filter => hide cards with no due date
      if (!card.due_date) return false;

      const timeDiff = this.timeDifference(card.due_date, amountAndType);
      return (before && timeDiff > 0) || (after && timeDiff < 0) ? cardId : false;
    },

    /**
     * This filtering method is applicable to all date-based filters.
     * @param {number} cardId
     * @param {'time_updated' | 'time_created'} field
     * @returns
     */
    isTime(cardId, field) {
      if (!Object.keys(this.currentFilters?.[field] || {}).length) {
        return cardId;
      }

      const { before, after } = this.currentFilters[field] || {};
      const amountAndType = (before || after).replace('now', '');
      const card = this.cards[cardId];

      const timeDiff = this.timeDifference(card[field] || 0, amountAndType);
      return (before && timeDiff > 0) || (after && timeDiff < 0) ? cardId : false;
    },

    isPriority(cardId) {
      if (this.currentFilters?.include?.priority?.length) {
        const card = this.cards[cardId];
        if (this.currentFilters.include.priority.includes(card.priority || 0)) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.exclude?.priority?.length) {
        const card = this.cards[cardId];
        if (this.currentFilters.exclude.priority.includes(card.priority || 0)) {
          return false;
        }
        return cardId;
      }
      return cardId;
    },

    isEstimate(cardId) {
      if (this.currentFilters?.include?.estimates?.length) {
        const card = this.cards[cardId];
        if (this.currentFilters.include.estimates.includes(card.estimate || 0)) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.exclude?.estimates?.length) {
        const card = this.cards[cardId];
        if (this.currentFilters.exclude.estimates.includes(card.estimate || 0)) {
          return false;
        }
        return cardId;
      }
      return cardId;
    },

    timeDifference(cardTimeCreated, amountAndType) {
      let amount = +amountAndType.slice(0, -1);
      const type = amountAndType.slice(-1) || 'd',
        filterDate = new Date();

      const typeMethod = { y: 'FullYear', M: 'Month', d: 'Date', w: 'Date', h: 'Hours' }[type];
      if (type === 'w') amount *= 7;

      filterDate[`set${typeMethod}`](filterDate[`get${typeMethod}`]() + amount);

      const cardDueDateTimestamp = cardTimeCreated * 1000,
        filterDateTimestamp = filterDate.getTime();

      return filterDateTimestamp - cardDueDateTimestamp;
    },

    isLink(cardId) {
      if (this.currentFilters?.include?.external_links?.length) {
        const card = this.cards[cardId];
        if (card.external_links) {
          if (
            card.external_links?.some((l) =>
              this.currentFilters.include.external_links.includes(l.type)
            )
          ) {
            return cardId;
          }
        }
        return false;
      }

      if (this.currentFilters?.exclude?.external_links?.length) {
        const card = this.cards[cardId];
        if (card.external_links) {
          if (
            card.external_links?.some((l) =>
              this.currentFilters.exclude.external_links.includes(l.type)
            )
          ) {
            return false;
          }
          return cardId;
        }
        // not sure if it should return all cards without any links
        // return false;
      }
      return cardId;
    },

    linkedCardFilter(cardId) {
      if (this.currentFilters?.include?.linked_cards?.length) {
        const card = this.cards[cardId];
        if (
          card.linked_cards?.some((c) =>
            this.currentFilters.include.linked_cards.includes(c.linked_card_type)
          )
        ) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.exclude?.linked_cards?.length) {
        const card = this.cards[cardId];
        if (
          card.linked_cards?.some((c) =>
            this.currentFilters.exclude.linked_cards.includes(c.linked_card_type)
          )
        ) {
          return false;
        }
        return cardId;
      }
      return cardId;
    },

    moveCardMessageHandler({
      listId,
      newIndex,
      oldIndex,
      cardId,
      boardId,
      type = ResourceType.Card,
    }) {
      if (listId !== this.listId) return;
      this.handleUpdateEvent({ newIndex, oldIndex, cardId, boardId, debounced: true, type });
    },

    addCardMessageHandler({
      listId,
      newIndex,
      oldIndex,
      cardId,
      boardId,
      projectId,
      originalListId,
      type = ResourceType.Card,
    }) {
      if (listId !== this.listId) return;
      this.handleAddEvent({
        newIndex,
        oldIndex,
        card: { id: cardId },
        originalListId,
        originalBoardId: boardId,
        projectId,
        debounced: true,
        type,
      });
    },

    handleUpdateEvent({
      newIndex,
      oldIndex,
      cardId,
      boardId,
      debounced = false,
      type = ResourceType.Card,
    }) {
      const listId = this.listId;
      const oldCardListId = this.cards[cardId].sprint_id
        ? `${boardId}-${this.cards[cardId].list_id}`
        : this.cards[cardId].list_id;
      // calculate new index position and original old index
      const calculatedNewIndex = this.getCalculatedNewIndex(newIndex);
      const calculatedOldIndex = this.originalListCards.indexOf(cardId);

      if (this.boardGroupBy !== groupByOption.status) {
        // update card order in grouped by list
        this.$store.dispatch('reorderCardsGroupByList', {
          boardId,
          listId,
          cardId,
          newIndex: calculatedNewIndex,
          oldIndex: calculatedOldIndex,
          groupBy: this.boardGroupBy,
        });
        return;
      }

      handleUpdateEventBoardView({
        newIndex: calculatedNewIndex,
        oldIndex: calculatedOldIndex,
        cardId,
        listId,
        boardId,
        storeDispatch: this.$store.dispatch,
        storeGetters: this.$store.getters,
      });

      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }
      this.updateTimeout = setTimeout(
        () => {
          // send update card request
          this.$store
            .dispatch('updateCard', {
              cardId,
              card: { position: calculatedNewIndex },
              type,
            })
            .catch((error) => {
              if (error === this.$constants.updateCardCancel) return;
              // revert old order
              handleUpdateEventBoardView({
                newIndex: calculatedOldIndex,
                oldIndex: calculatedNewIndex,
                cardId,
                listId: oldCardListId,
                boardId,
                storeDispatch: this.$store.dispatch,
                storeGetters: this.$store.getters,
              });
            });
        },
        debounced ? 400 : 0
      );
    },

    getCalculatedNewIndex(newIndex) {
      let calculatedNewIndex = newIndex;
      if (this.filteredListCards.length < newIndex) {
        calculatedNewIndex = newIndex - 1;
      }
      if (this.lessFilteredCards && this.originalListCards.length) {
        // when there are no filtered cards set to last position in original list
        if (this.filteredListCards.length === 0) {
          calculatedNewIndex = this.originalListCards.length;
        }

        // when there is one card in filtered list
        if (this.filteredListCards.length === 1) {
          const referentCardId = this.filteredListCards[0];
          const referentCardPosition = this.originalListCards.indexOf(referentCardId);
          // if the new index is 0, calculate referent card position
          calculatedNewIndex = referentCardPosition;

          if (newIndex > 0) {
            calculatedNewIndex = referentCardPosition + 1;
          }
        }

        // when there are more than one card in filtered list
        if (this.filteredListCards.length > 1) {
          let referentCardId = this.filteredListCards[calculatedNewIndex];
          let referentCardPosition = this.originalListCards.indexOf(referentCardId);

          if (referentCardPosition < 0) {
            referentCardId = this.filteredListCards[calculatedNewIndex - 1];
            referentCardPosition = this.originalListCards.indexOf(referentCardId) + 1;
          }

          calculatedNewIndex = referentCardPosition;
        }
      }
      return calculatedNewIndex;
    },

    handleAddEvent({
      newIndex,
      oldIndex,
      card,
      originalListId,
      originalBoardId,
      projectId = card.project_id,
      debounced = false,
      type = ResourceType.Card,
    }) {
      const destinationListId = this.listId;
      const destinationBoardId = this.currentBoardId;
      const destinationBoard = this.$store.getters.getFlatBoards[destinationBoardId];

      const boardType = this.embeddedBoardAttributes?.boardType || 'board';

      if (this.boardGroupBy !== groupByOption.status) {
        const fullCard = this.$store.getters.getCards[card.id];
        this.handleGroupByDrag(
          originalListId,
          destinationListId,
          fullCard,
          this.boardGroupBy,
          newIndex
        );
        return;
      }

      // calculate new index position and original old index
      const calculatedOldIndex = this.$store.getters.getListCards[originalListId].indexOf(card.id);
      const calculatedNewIndex = this.getCalculatedNewIndex(newIndex);

      handleAddEventBoardView({
        newIndex: calculatedNewIndex,
        oldIndex: calculatedOldIndex,
        cardId: card.id,
        listId: destinationListId,
        originalListId,
        destinationBoardId,
        originalBoardId,
        storeDispatch: this.$store.dispatch,
        storeGetters: this.$store.getters,
      });

      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = setTimeout(
        () => {
          this.addCardToList({
            listId: destinationListId,
            cardId: card.id,
            position: calculatedNewIndex,
            itemId: destinationBoard.orginalId || destinationBoardId,
            ...(boardType === ResourceType.Sprint && { projectId }),
            type,
            originalListId,
          }).catch((error) => {
            if (error === this.$constants.updateCardCancel) return;
            handleAddEventBoardView({
              newIndex: calculatedOldIndex,
              oldIndex: calculatedNewIndex,
              cardId: card.id,
              listId: originalListId,
              originalListId: destinationListId,
              destinationBoardId: originalBoardId,
              originalBoardId: destinationBoardId,
              storeDispatch: this.$store.dispatch,
              storeGetters: this.$store.getters,
            });
            // restore lasthovered list id if different from originalListId
            if (this.$store.getters.lastHoveredListId !== originalListId) {
              this.$store.dispatch('lastHoveredListId', originalListId);
            }
          });
        },
        debounced ? 400 : 0
      );
    },

    clearProjectPreview() {
      this.$store.dispatch('displayProjectPreviewBoards', false);
      this.$store.dispatch('setProjectPreview', {});
    },

    isDropTargetProject() {
      if (this.dropTargetId.includes('pr-')) {
        this.$store.dispatch('displayProjectPreviewBoards', false);
        this.$store.dispatch('setProjectPreview', {});
        const notification = {
          message: this.translate('cardCannotBeMovedDirectlyToSpace'),
          status: this.$constants.uiNotificationStatuses.error,
          duration: 3000,
        };
        this.$store.dispatch('addUiNotification', notification);
        return true;
      }
      return false;
    },

    addCard(evt) {
      if (this.isDropTargetProject()) {
        return;
      }
      if (this.dropTargetId === '') {
        this.clearProjectPreview();
        return;
      }
      const { item, newDraggableIndex, oldDraggableIndex } = evt;
      const { getCards } = this.$store.getters;

      const card = getCards[item.dataset.cardId];

      this.handleAddEvent({
        card,
        newIndex: newDraggableIndex,
        oldIndex: oldDraggableIndex,
        originalListId: item.dataset.listId,
        originalBoardId: item.dataset.boardId,
        type: card.type,
      });
    },
    moveCard(evt) {
      if (this.isDropTargetProject()) {
        return;
      }
      if (this.dropTargetId === '') {
        this.clearProjectPreview();
        return;
      }

      const { item, newDraggableIndex, oldDraggableIndex } = evt;
      const type =
        item.dataset.boardId === this.$constants.epicsBoardId
          ? ResourceType.Epic
          : ResourceType.Card;

      this.handleUpdateEvent({
        boardId: item.dataset.boardId,
        cardId: item.dataset.cardId,
        newIndex: newDraggableIndex,
        oldIndex: oldDraggableIndex,
        type,
      });
    },
    checkMove(evt) {
      if (
        evt.to.className.includes('draggable-cards') ||
        evt.to.className.includes('droppable-header')
      ) {
        const id = `l-${evt.to.dataset.listId}`;
        if (id !== this.dropTargetId) {
          this.$store.dispatch('setDropTargetId', id);
        }
      }
      return true;
    },

    addCardToList({
      listId,
      cardId,
      position,
      itemId = null,
      type = ResourceType.Card,
      projectId = null,
      originalListId,
    }) {
      const boardType = this.embeddedBoardAttributes?.boardType || ResourceType.Board;
      const listIdForReq =
        boardType === ResourceType.Board ? listId : this.$store.getters.lists[listId]?.orginalId; // for sprints

      return this.$store.dispatch('updateCard', {
        cardId,
        card: {
          position,
          list_id: listIdForReq,
          ...(boardType === ResourceType.Board && { board_id: itemId }),
          ...(boardType !== ResourceType.Board && { sprint_id: itemId }),
          ...(projectId && { project_id: projectId }),
        },
        oldListId: originalListId,
        type,
      });
    },

    isParentCard(cardId) {
      if (this.currentFilters?.include?.parent_cards?.length) {
        const card = this.cards[cardId];
        if (
          card?.parent_card?.card_id &&
          this.currentFilters.include.parent_cards.includes(card.parent_card.card_id)
        ) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.exclude?.parent_cards?.length) {
        const card = this.cards[cardId];
        if (
          card?.parent_card?.card_id &&
          this.currentFilters.exclude.parent_cards.includes(card.parent_card.card_id)
        ) {
          return false;
        }
        return cardId;
      }
      return cardId;
    },

    isEpic(cardId) {
      if (this.currentFilters?.include?.epics?.length) {
        const card = this.cards[cardId];
        if (card?.epic?.id && this.currentFilters.include.epics.includes(card.epic.id)) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.exclude?.epics?.length) {
        const card = this.cards[cardId];
        if (card?.epic?.id && this.currentFilters.exclude.epics.includes(card.epic.id)) {
          return false;
        }
        return cardId;
      }
      return cardId;
    },

    isProject(cardId) {
      if (this.currentFilters?.include?.projects?.length) {
        const card = this.cards[cardId];
        if (this.currentFilters.include.projects.includes(card.project_id)) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.exclude?.projects?.length) {
        const card = this.cards[cardId];
        if (this.currentFilters.exclude.projects.includes(card.project_id)) {
          return false;
        }
        return cardId;
      }
      return cardId;
    },

    isBoard(cardId) {
      if (this.currentFilters?.include?.boards?.length) {
        const card = this.cards[cardId];
        if (this.currentFilters.include.boards.includes(card.board_id)) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.exclude?.boards?.length) {
        const card = this.cards[cardId];
        if (this.currentFilters.exclude.boards.includes(card.board_id)) {
          return false;
        }
        return cardId;
      }
      return cardId;
    },

    isArchivedCard(cardId) {
      if (this.currentFilters?.is_archived) {
        const card = this.cards[cardId];
        if (card?.archived) {
          return cardId;
        }
        return false;
      }
      if (this.currentFilters?.is_archived === false) {
        const card = this.cards[cardId];
        if (!card?.archived) {
          return cardId;
        }
        return false;
      }
      return cardId;
    },
  },
};
